import { useCallback, useEffect, useRef, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import dateTimeService from "app/services/date-time/dateTimeService";

import { SignalRecommenderFilesList } from "../components/SignalRecommenderFilesList/SignalRecommenderFilesList";
import { UploadDealSignalsButton } from "../components/UploadDealSignalsButton/UploadDealSignalsButton";
import { DEAL_SIGNAL_DATE_FORMAT, DEAL_SIGNAL_REFRESH_MIN_SPINNER_MS } from "../constants";
import { useDealSignalEnrichment } from "../hooks/useDealSignalEnrichment";
import { useDealSignalStatusToasts } from "../hooks/useDealSignalStatusToasts";
import { useDownloadDealSignalFileMutation } from "../query/mutations/useDownloadDealSignalFileMutation";
import { useSignalRecommenderFilesQuery } from "../query/queries/useSignalRecommenderFilesQuery";
import signalRecommenderTrackingService from "../services/tracking";
import type { DealSignalUploadInput, SignalRecommenderFile } from "../types/model";
import signalRecommenderI18nKeys from "../utils/i18n";

import UploadDealSignalsModalContainer from "./UploadDealSignalsModal";

export const SignalRecommenderContainer = () => {
    const { translate } = useTranslate();
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

    const { data, isLoading, isError, refetch } = useSignalRecommenderFilesQuery();
    const { isUploading, startEnrichment } = useDealSignalEnrichment();
    const {
        mutate: downloadEnrichedFile,
        isPending: isDownloading,
        variables: downloadVariables,
    } = useDownloadDealSignalFileMutation();

    const [refreshingFileId, setRefreshingFileId] = useState<number | null>(null);
    const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const files = data?.files;

    useDealSignalStatusToasts(files);

    // `variables` is only typed alongside `isPending` inside the mutation result's own union; destructuring
    // severs that link, so the optional read is the honest one.
    const downloadingFileId = isDownloading ? (downloadVariables?.id ?? null) : null;

    // A failed background poll sets `error` while the previous rows are still cached; replacing a populated
    // list with an error state over one flaky tick would be worse than showing it slightly stale.
    const hasFailedWithNothingToShow = isError && !files?.length;

    const nextUploadDate = data?.nextUploadAllowedAt;
    const isUploadDisabled = isLoading || isUploading || hasFailedWithNothingToShow || nextUploadDate != null;

    const cooldownTooltip = nextUploadDate
        ? signalRecommenderI18nKeys.button.uploadCsvCooldownTooltip({
              date: dateTimeService.formatWithUTCMoment(nextUploadDate, DEAL_SIGNAL_DATE_FORMAT),
          })
        : null;

    const uploadDisabledTooltip = cooldownTooltip
        ? translate(cooldownTooltip.key, cooldownTooltip.replacements)
        : undefined;

    // Before the first run there is no history to head, so the upload moves into the empty state and
    // the header keeps only its heading.
    const isHistoryEmpty = !isLoading && !hasFailedWithNothingToShow && files?.length === 0;

    const handleDownload = ({ id, fileName }: SignalRecommenderFile) => {
        signalRecommenderTrackingService.trackDownloadFile();
        downloadEnrichedFile({ id, fileName });
    };

    const handleRetry = useCallback(() => {
        signalRecommenderTrackingService.trackRetryHistory();
        void refetch();
    }, [refetch]);

    // One endpoint returns the whole history, so only the indicator is per-row. A stalled run usually
    // comes back unchanged, and without a floor on the spinner the button would look dead.
    const handleRefresh = useCallback(
        async ({ id }: SignalRecommenderFile) => {
            signalRecommenderTrackingService.trackRefreshFileStatus();
            setRefreshingFileId(id);

            try {
                await refetch();
            } finally {
                refreshTimeoutRef.current = setTimeout(
                    () => setRefreshingFileId(null),
                    DEAL_SIGNAL_REFRESH_MIN_SPINNER_MS,
                );
            }
        },
        [refetch],
    );

    useEffect(
        () => () => {
            if (refreshTimeoutRef.current) {
                clearTimeout(refreshTimeoutRef.current);
            }
        },
        [],
    );

    const handleOpenUploadModal = useCallback(() => {
        signalRecommenderTrackingService.trackOpenUploadModal();
        setIsUploadModalOpen(true);
    }, []);

    // `EmptyState` mounts its CTA as a component rather than rendering a node, so an inline arrow here
    // would remount the button and its tooltip on every render of this container.
    const renderUploadButton = useCallback(
        () => (
            <UploadDealSignalsButton
                isDisabled={isUploadDisabled}
                disabledTooltip={uploadDisabledTooltip}
                onClick={handleOpenUploadModal}
            />
        ),
        [handleOpenUploadModal, isUploadDisabled, uploadDisabledTooltip],
    );

    // The upload is uncancellable once it leaves and its result reaches the history list on its own, so
    // there is nothing left for the modal to stay open for.
    const handleUploadSubmit = (input: DealSignalUploadInput) => {
        setIsUploadModalOpen(false);
        startEnrichment(input);
    };

    return (
        <Stack gap="24px">
            <Flex
                alignItems="center"
                justifyContent="space-between"
            >
                <Text
                    fontVariant="h5"
                    color={colorsPalettes.carbon[400]}
                >
                    {translate(signalRecommenderI18nKeys.list.title)}
                </Text>

                {isHistoryEmpty ? null : renderUploadButton()}
            </Flex>

            <SignalRecommenderFilesList
                files={files ?? []}
                isLoading={isLoading}
                isError={hasFailedWithNothingToShow}
                downloadingFileId={downloadingFileId}
                refreshingFileId={refreshingFileId}
                emptyStateCta={renderUploadButton}
                onDownload={handleDownload}
                onRefresh={handleRefresh}
                onRetry={handleRetry}
            />

            {isUploadModalOpen ? (
                <UploadDealSignalsModalContainer
                    isOpen
                    onClose={() => setIsUploadModalOpen(false)}
                    onSubmit={handleUploadSubmit}
                />
            ) : null}
        </Stack>
    );
};
